// overall renderer -> processComponent -> swimlaneComponent -> Collapse component -> step component -> collapse component -> field component

import { devLogger } from "@certa/common/utils/devLogger";
import { mainLayer } from "../../pixi-components/mainContainer/mainContainer";
import type { CoreComponent } from "../coreComponents/coreComponents.types";
import type {
  BoxProps,
  CombinedProps,
  IconProps,
  RhombusProps,
  TextProps
} from "../coreComponents/types";
import type { FSC, SCNode } from "./fsc.types";
import { createRenderManager, type SCNodeWithMetaData } from "./renderManager";
import { createBox } from "../coreComponents/Box";
import { createIcon } from "../coreComponents/Icon";
import { createText } from "../coreComponents/Text";
import { renderFlags } from "../layoutEngine/renderFlags";
import { calculateLayout } from "../layoutEngine/layoutCalculator";
import { getDragContext } from "../interactionEngine/dragContext";
import { spatialRegistry } from "../interactionEngine/spatialRegistry";
import { createRhombus } from "../coreComponents/Rhombus";
import { getViewport } from "../../pixi-components/layers/viewport/viewport.layer";

const instances: Record<string, CoreComponent> = {};
type NodeStore = {
  currentTree: SCNode | null;
  wipTree: SCNode | null;
};
const nodeStore: NodeStore = {
  currentTree: null,
  wipTree: null
};
let seq = 0;
let renderManager: ReturnType<typeof createRenderManager> | null = null;
/**
 * Primary renderer that takes care of:
 * 1. Creating the new tree
 * 2. Comparing the new tree with the old tree
 * 3. Create the final tree that re-uses the elements from old tree that are
 *    still valid and adds the new/updated elements from the new tree
 * @param entryPoint head of the new tree
 * @param props props to be passed to the main component
 */
export const mainRender = <T>(entryPoint: FSC<T>, props: any) => {
  const rootNode = entryPoint(props);
  if (!rootNode) return;
  if (!renderManager) {
    renderManager = createRenderManager();
  }

  const t1 = performance.now();
  const result = renderManager.traverse(rootNode);
  const t2 = performance.now();

  for (const addedKey of result.added) {
    const node = result.keyMap.get(addedKey);
    if (!node) {
      throw Error(`Node not found for key: ${addedKey}`);
    }

    if (instances[addedKey]) {
      throw Error(`Instance already exists for key: ${addedKey}`);
    }

    const instance = instantiateNode(node);
    // devLogger.log(`Adding instance for key: ${addedKey}`);
    if (addedKey === rootNode.key) {
      mainLayer.addChild(instance.pixiComponent);
    } else {
      // attach yoga node
      const parentInstance = node.parentKey ? instances[node.parentKey] : null;
      if (!parentInstance) {
        throw Error(`Parent instance not found for key: ${node.parentKey}`);
      }

      // TODO: Remove this try-catch once we analyse the root cause in S172
      try {
        parentInstance.yogaNode.insertChild(instance.yogaNode, node.index);
        parentInstance.pixiComponent.addChild(instance.pixiComponent);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          `Failed to add child instance: ${instance.props.key} to parent: ${parentInstance.props.key} at index: ${node.index} of node: ${node.key}`
        );
        throw Error("Error adding child to parent");
      }
    }
  }

  const instancesToDestroy = [];

  for (const removedKey of result.removed) {
    const instance = instances[removedKey];
    if (!instance) {
      throw Error(`Instance not found for key: ${removedKey}`);
    }

    if (removedKey === rootNode.key) {
      mainLayer.removeChildren();
    }

    // not directly freeing here because it could have children which
    // probably need to move to other parents, so we only collect
    // and free later after performed all other operations.
    instancesToDestroy.push(instance);
    const layoutValues = instance.getLayoutValues();
    spatialRegistry.remove({ key: instance.props.key, layoutValues });
    delete instances[removedKey];
  }
  const updatedInstances = new Set<CoreComponent>();

  for (const movedKey of result.moved) {
    const node = result.keyMap.get(movedKey);
    if (!node) {
      throw Error(`Node not found for key: ${movedKey}`);
    }

    const instance = instances[movedKey];
    if (!instance) {
      throw Error(`Instance not found for key: ${movedKey}`);
    }

    // Remove from current Yoga parent
    const currentYogaParent = instance.yogaNode.getParent();
    if (!currentYogaParent) {
      throw Error(`Yoga Parent instance not found for key: ${node.parentKey}`);
    }
    currentYogaParent.removeChild(instance.yogaNode);

    // Remove from current Pixi parent
    instance.pixiComponent.parent.removeChild(instance.pixiComponent);

    if (!node.parentKey) {
      throw Error(`Parent key not found for key: ${movedKey}`);
    }

    // Add to new Yoga parent
    const newParentInstance = instances[node.parentKey];
    if (!newParentInstance) {
      throw Error(`New Parent instance not found for key: ${node.parentKey}`);
    }
    newParentInstance.yogaNode.insertChild(instance.yogaNode, node.index);

    // Add to new Pixi parent
    newParentInstance.pixiComponent.addChild(instance.pixiComponent);

    instance.updateProps(node.props as CombinedProps);
    updatedInstances.add(instance);
  }
  for (const updatedKey of result.updated) {
    const node = result.keyMap.get(updatedKey);
    if (!node) {
      throw Error(`Node not found for key: ${updatedKey}`);
    }

    const instance = instances[updatedKey];
    if (!instance) {
      throw Error(`Instance not found for key: ${updatedKey}`);
    }

    instance.updateProps(node.props as CombinedProps);
    updatedInstances.add(instance);
  }

  // finally free all the nodes that are not needed anymore
  for (const instance of instancesToDestroy) {
    instance.destroy();
  }

  let dirtyNodes = new Set([rootNode.key]);
  if (!result.firstRender) {
    dirtyNodes = calculateLayout({
      rootNodeKey: rootNode.key,
      modifiedNodeKeys: [...result.added, ...result.updated, ...result.moved],
      keyMap: result.keyMap
    });
  }

  if (dirtyNodes.size > 0) {
    const dragContext = getDragContext();
    dirtyNodes.forEach(key => {
      const instance = instances[key];
      if (!instance) {
        throw Error(`Instance not found for key: ${key}`);
      }
      if (instance === dragContext.node) {
        return;
      }
      updateLayoutAndRender({
        key,
        keyMap: result.keyMap,
        zIndex: instance.getZIndex() || 1
      });
    });
    const viewport = getViewport();
    viewport.dirty = true;
  }

  // We need this for "selected" highlight to show up
  // as it doesn't change the layout positions and hence
  // can get blocked if no node is marked as dirty
  if (updatedInstances.size > 0) {
    for (const updatedInstance of updatedInstances) {
      updatedInstance.render();
    }
  }

  const t3 = performance.now();

  devLogger.log(
    `RENDER ${seq} SEQUENCE ENDED
  🍄 ➕${result.added.size} ➖${result.removed.size} 🔀 ${result.moved.size} ♻︎ ${result.existing.size}
  traverse: ${(t2 - t1).toFixed(2)}ms
  update: ${(t3 - t2).toFixed(2)}ms`
  );

  seq++;

  nodeStore.currentTree = rootNode;
  renderFlags.layoutDirty = true;
};

const updateLayoutAndRender = ({
  key,
  keyMap,
  zIndex
}: {
  key: string;
  keyMap: Map<string, SCNodeWithMetaData>;
  zIndex: number;
}) => {
  const node = keyMap.get(key);
  if (!node) {
    throw Error(`Node not found for key: ${key}`);
  }

  const parentLayout = node.parentKey
    ? instances[node.parentKey].getLayoutValues()
    : null;
  const offsetX = parentLayout ? parentLayout.left + parentLayout.offsetX : 0;
  const offsetY = parentLayout ? parentLayout.top + parentLayout.offsetY : 0;

  const instance = instances[key];
  if (!instance) {
    throw Error(`Instance not found for key: ${key}`);
  }
  const children = node.children;

  const lastLayoutValues = instance.getLayoutValues();
  const layoutValues = instance.yogaNode.getComputedLayout();
  instance.setLayoutValues({
    left: layoutValues.left,
    top: layoutValues.top,
    width: layoutValues.width,
    height: layoutValues.height,
    offsetX: offsetX,
    offsetY: offsetY,
    zIndex: zIndex
  });
  spatialRegistry.update({
    key: instance.props.key,
    lastLayoutValues,
    nextLayoutValues: instance.getLayoutValues()
  });
  children.forEach(child => {
    updateLayoutAndRender({
      key: child.key,
      keyMap,
      zIndex: zIndex + 1
    });
  });
  instance.render();
};

/**
 * Instantiates the node and adds it to the instances
 * @param node
 * @returns
 */
const instantiateNode = (node: SCNode) => {
  switch (node.componentKey) {
    case "Box":
      instances[node.key] = createBox(node.props as BoxProps);
      break;
    case "Rhombus":
      instances[node.key] = createRhombus(node.props as RhombusProps);
      break;
    case "Text":
      instances[node.key] = createText(node.props as TextProps);
      break;
    case "Icon":
      instances[node.key] = createIcon(node.props as IconProps);
      break;
    default:
      throw Error(`Unknown component: ${node.componentKey}`);
  }

  return instances[node.key];
};

export const getElementByKey = (key: string): CoreComponent | null => {
  return instances[key] || null;
};

export const mainUnrender = () => {
  Object.entries(instances).forEach(([key, instance]) => {
    delete instances[key];
    // instance.destroy(); // This is handled by cleanup() when it removes mainLayer's all children
  });
  renderManager?.destroy();
  // We need to reset the currentTree and wipTree otherwise
  // when the next render happens, it'll try to compare the
  // currentTree with the wipTree which is not what we want
  // For a fresh render we want to reset the tree states to null
  nodeStore.currentTree = null;
  nodeStore.wipTree = null;
};

export const getInstances = () => instances; // Do we freeze it?
export const isComponentMounted = (key: string) => !!instances[key];
export const getRootNode = () => nodeStore.currentTree;
export const getNodeForKey = (key: string) => renderManager?.getNodeForKey(key);
export const getPixiParentInstance = (key: string) => {
  const parentNodeKey = renderManager?.getNodeForKey(key)?.parentKey;
  if (!parentNodeKey) {
    throw Error(
      `Parent node key not found for key: ${key} (parent key: ${key})`
    );
  }
  const parentInstance = instances[parentNodeKey];
  if (!parentInstance) {
    throw Error(
      `Parent instance not found for key: ${key} (parent key: ${parentNodeKey})`
    );
  }
  return parentInstance;
};
